import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, AlertOctagon, MessageSquare, Clock, CheckCircle2, 
  AlertTriangle, XCircle, Trash2, X, Search, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { projectAPI } from '../lib/api';

const statusOptions = [
  { value: 'active', label: 'Active', color: 'text-amber-400 bg-amber-500/10 border-amber-500/20', icon: AlertOctagon },
  { value: 'investigating', label: 'Investigating', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', icon: AlertTriangle },
  { value: 'resolved', label: 'Resolved', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', icon: CheckCircle2 },
  { value: 'closed', label: 'Closed', color: 'text-gray-400 bg-gray-500/10 border-gray-500/20', icon: XCircle },
];

const severityOptions = ['Critical', 'High', 'Medium', 'Low'];

export default function ShowstoppersPanel({ projectId, canEdit = true }) {
  const [showstoppers, setShowstoppers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedSS, setSelectedSS] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [newSS, setNewSS] = useState({
    title: '',
    description: '',
    severity: 'High',
    status: 'active',
    reported_by: '',
    affected_areas: '',
  });

  useEffect(() => {
    loadShowstoppers();
  }, [projectId]);

  const loadShowstoppers = async () => {
    try {
      const res = await projectAPI.getShowstoppers(projectId);
      setShowstoppers(res.data || []);
    } catch (err) {
      console.log('Loading showstoppers:', err);
      setShowstoppers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSS = async () => {
    if (!newSS.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      await projectAPI.addShowstopper(projectId, newSS);
      setShowAddModal(false);
      setNewSS({ title: '', description: '', severity: 'High', status: 'active', reported_by: '', affected_areas: '' });
      await loadShowstoppers();
      toast.success('Showstopper added');
    } catch (err) {
      toast.error('Failed to add showstopper');
    }
  };

  const handleUpdateStatus = async (ssId, newStatus) => {
    try {
      await projectAPI.updateShowstopper(projectId, ssId, { status: newStatus });
      await loadShowstoppers();
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (ssId) => {
    try {
      await projectAPI.deleteShowstopper(projectId, ssId);
      setSelectedSS(null);
      await loadShowstoppers();
      toast.success('Deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleAddComment = async () => {
    if (!chatMessage.trim() || !selectedSS) return;
    try {
      const updatedComments = [...(selectedSS.comments || []), {
        id: Date.now().toString(),
        text: chatMessage,
        created_at: new Date().toISOString(),
        user: 'Current User'
      }];
      await projectAPI.updateShowstopper(projectId, selectedSS.id, { comments: updatedComments });
      setChatMessage('');
      await loadShowstoppers();
      setSelectedSS(prev => ({ ...prev, comments: updatedComments }));
      toast.success('Comment added');
    } catch (err) {
      toast.error('Failed to add comment');
    }
  };

  const filteredSS = showstoppers.filter(ss => {
    const matchesSearch = ss.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          ss.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || ss.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const opt = statusOptions.find(s => s.value === status) || statusOptions[0];
    const Icon = opt.icon;
    return (
      <Badge className={`${opt.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {opt.label}
      </Badge>
    );
  };

  const activeCount = showstoppers.filter(ss => ss.status === 'active').length;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="h-full flex" data-testid="showstoppers-panel">
      {/* List */}
      <div className={`${selectedSS ? 'w-1/2 border-r border-[var(--surface-border)]' : 'w-full'} flex flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-[var(--surface-border)]">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-left">Showstoppers</h2>
                {activeCount > 0 && (
                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                    {activeCount} Active
                  </Badge>
                )}
              </div>
              <p className="text-xs opacity-60 text-left">Critical issues blocking progress</p>
            </div>
            {canEdit && (
              <Button size="sm" onClick={() => setShowAddModal(true)}>
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Report
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 opacity-40" />
              <Input
                placeholder="Search showstoppers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* List */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3">
            {filteredSS.length === 0 ? (
              <div className="text-center py-12 opacity-60">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-400 opacity-50" />
                <p className="text-sm">No showstoppers found</p>
                <p className="text-xs mt-1 text-emerald-400">Project is clear!</p>
              </div>
            ) : (
              filteredSS.map((ss) => (
                <div
                  key={ss.id}
                  onClick={() => setSelectedSS(ss)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedSS?.id === ss.id
                      ? 'border-[var(--accent-indigo)] bg-[var(--accent-indigo)]/5'
                      : 'border-[var(--surface-border)] bg-[var(--surface-card)] hover:border-[var(--accent-indigo)]/30'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(ss.status)}
                      <Badge variant="outline" className={`text-[10px] ${
                        ss.severity === 'Critical' ? 'text-red-400 border-red-500/30' :
                        ss.severity === 'High' ? 'text-amber-400 border-amber-500/30' : ''
                      }`}>
                        {ss.severity}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 opacity-50">
                      <MessageSquare className="w-3 h-3" />
                      <span className="text-xs">{ss.comments?.length || 0}</span>
                    </div>
                  </div>
                  <h3 className="font-medium text-left mb-1">{ss.title}</h3>
                  {ss.description && (
                    <p className="text-sm opacity-60 text-left line-clamp-2">{ss.description}</p>
                  )}
                  <div className="flex items-center justify-between mt-2 text-xs opacity-40">
                    <span>{ss.reported_by || 'Unknown'}</span>
                    <span>{new Date(ss.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Detail/Chat Panel */}
      {selectedSS && (
        <div className="w-1/2 flex flex-col">
          <div className="p-4 border-b border-[var(--surface-border)]">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {getStatusBadge(selectedSS.status)}
                  <Badge variant="outline">{selectedSS.severity}</Badge>
                </div>
                <h3 className="text-lg font-semibold text-left">{selectedSS.title}</h3>
              </div>
              <div className="flex gap-1">
                {canEdit && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDelete(selectedSS.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedSS(null)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            {selectedSS.description && (
              <p className="text-sm opacity-70 mt-2 text-left">{selectedSS.description}</p>
            )}
            {selectedSS.affected_areas && (
              <p className="text-xs opacity-50 mt-2 text-left"><strong>Affected:</strong> {selectedSS.affected_areas}</p>
            )}
            {canEdit && (
              <div className="flex gap-2 mt-3">
                {statusOptions.map(s => (
                  <Button
                    key={s.value}
                    variant={selectedSS.status === s.value ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => handleUpdateStatus(selectedSS.id, s.value)}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Comments/Chat */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              {(!selectedSS.comments || selectedSS.comments.length === 0) ? (
                <div className="text-center py-8 opacity-50">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No comments yet</p>
                </div>
              ) : (
                selectedSS.comments.map((comment, i) => (
                  <div key={comment.id || i} className="p-3 rounded-lg bg-[var(--surface-card)] border border-[var(--surface-border)]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{comment.user}</span>
                      <span className="text-xs opacity-40">{new Date(comment.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-left">{comment.text}</p>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          {/* Chat Input */}
          {canEdit && (
            <div className="p-4 border-t border-[var(--surface-border)]">
              <div className="flex gap-2">
                <Input
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <Button onClick={handleAddComment} disabled={!chatMessage.trim()}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-card)] rounded-xl border border-[var(--surface-border)] w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Report Showstopper</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Title *</label>
                <Input value={newSS.title} onChange={(e) => setNewSS({ ...newSS, title: e.target.value })} placeholder="Brief description of the blocker" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Description</label>
                <Textarea value={newSS.description} onChange={(e) => setNewSS({ ...newSS, description: e.target.value })} placeholder="Detailed description and context" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Severity</label>
                  <Select value={newSS.severity} onValueChange={(v) => setNewSS({ ...newSS, severity: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {severityOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block text-left">Reported By</label>
                  <Input value={newSS.reported_by} onChange={(e) => setNewSS({ ...newSS, reported_by: e.target.value })} placeholder="Your name" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-left">Affected Areas</label>
                <Input value={newSS.affected_areas} onChange={(e) => setNewSS({ ...newSS, affected_areas: e.target.value })} placeholder="e.g., Login, Payment, API" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleAddSS}>Report Showstopper</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
